/**
 * Field-side capture handler.
 *
 * Lives inside <LiveKitRoom>. On mount, registers a CommandBus handler for
 * `command/capture.snapshot`. When the operator triggers a snapshot, this
 * runs (on the field's device):
 *   1. Find the local camera <video> element.
 *   2. Acquire a fresh GPS fix via navigator.geolocation. Permission is
 *      cached from the consent screen's user-gesture call so this runs
 *      without prompting on iOS Safari.
 *   3. Upload via the field-session token.
 *   4. Reply with `{evidence_id, sha256, status}` so the operator's
 *      EvidenceList updates immediately.
 */
import { useRoomContext } from "@livekit/components-react";
import { useEffect } from "react";

import { CommandBus, type Envelope, makeLiveKitTransport } from "@/lib/commandBus";
import { useConnectionQualityStore } from "@/lib/connectionQuality";
import { acquireGeolocation, type GeoFix } from "@/lib/geolocation";
import { captureFrameAsBlob, uploadFieldSnapshot } from "@/lib/evidence";

type Props = { sessionId: string; fieldSessionToken: string };

export function FieldCaptureHandler({ sessionId, fieldSessionToken }: Props) {
  const room = useRoomContext();

  useEffect(() => {
    if (!room) return;
    const transport = makeLiveKitTransport(room, sessionId);
    const bus = new CommandBus(transport, sessionId, {
      kind: "participant",
      id: room.localParticipant.identity,
    });

    const off = bus.on<{ kind?: string; mime?: string }, AckPayload>(
      "command/capture.snapshot",
      async (env) => handleCapture(env, sessionId, fieldSessionToken),
    );

    return () => {
      off();
      bus.dispose();
    };
  }, [room, sessionId, fieldSessionToken]);

  return null;
}

type AckPayload = {
  ok: boolean;
  evidence_id?: string;
  sha256?: string;
  status?: string;
  error?: string;
};

async function handleCapture(
  env: Envelope<{ kind?: string; mime?: string }>,
  sessionId: string,
  fieldSessionToken: string,
): Promise<AckPayload> {
  void env;
  const video = pickLocalCameraVideo();
  if (!video) {
    return { ok: false, error: "no local camera track" };
  }

  // Capture-time GPS — relies on consent-screen permission having been
  // granted (iOS Safari caches the answer for the page session).
  let geo: GeoFix | null = null;
  const outcome = await acquireGeolocation();
  if (outcome.ok) geo = outcome.fix;

  // Same tier the live capture resolution is adapting to (see
  // FieldCommandsHandler's ConnectionQualityChanged listener) — a
  // struggling connection gets a smaller JPEG too, not just a smaller
  // video feed, so the upload doesn't undo the resolution's savings.
  const tier = useConnectionQualityStore.getState().tier;
  const frame = await captureFrameAsBlob(
    video,
    "image/jpeg",
    tier === "low" ? 0.65 : 0.85,
  );
  const evidence = await uploadFieldSnapshot({
    sessionId,
    frame,
    fieldSessionToken,
    geo: geo
      ? {
          lat: geo.lat,
          lng: geo.lng,
          accuracy_m: geo.accuracy_m,
          source: geo.source,
          timestamp: geo.timestamp,
        }
      : { source: "unknown" },
  });

  return {
    ok: true,
    evidence_id: evidence.id,
    sha256: evidence.sha256,
    status: evidence.status,
  };
}

function pickLocalCameraVideo(): HTMLVideoElement | null {
  // Local participant publishes one camera track; LiveKit's components
  // render it as a tile with `data-lk-local-participant="true"` and
  // `data-lk-source="camera"`.
  return (
    document.querySelector<HTMLVideoElement>(
      'video[data-lk-source="camera"][data-lk-local-participant="true"]',
    ) ??
    document.querySelector<HTMLVideoElement>(
      "video[data-lk-source=\"camera\"]",
    )
  );
}